import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, IconSpinner, NamedLink } from '../../../../components';
import { getRecentMarketplaceEvents } from '../../../../util/api';
import { getTimeDiffRepresentation } from '../../../../util/time';
import css from './SectionNewActivities.css';
import { createResourceLocatorString } from '../../../../util/routes';
import routeConfiguration from '../../../../routeConfiguration';
import { usePublicEventContent } from './usePublicEventContent';

const ActivityItem = ({ item }) => {
    const [styles, setStyles] = useState({});

    const { days, hours, minutes } = getTimeDiffRepresentation(
        new Date(item.createdAt),
        new Date(),
        {
            prefix: 'Vor',
        }
    );

    useEffect(() => {
        setStyles({ opacity: 1 });
    }, []);

    const publicEventContent = usePublicEventContent(item);

    if (!publicEventContent) return null;

    const { icon, publicImage, description, link } = publicEventContent;

    return link ? (
        <div className={css.activityContainer} style={styles}>
            <NamedLink {...link} isNotRouterLink>
                <div className={css.activityContent}>
                    <div className={css.imagesHolder}>
                        {publicImage}
                        <div className={css.iconHolder}>{icon}</div>
                    </div>
                    {description || null}
                </div>
                <p className={css.activityLocationDate}>
                    <span>{item.publicLocation}</span>
                    <span className={css.delimiter}>•</span>
                    <span>{days || hours || minutes || ''}</span>
                </p>
            </NamedLink>
        </div>
    ) : null;
};

const SectionContent = ({ activities, history }) => {
    const currentUser = useSelector(s => s.user.currentUser);

    const currentUserDefined = currentUser?.id?.uuid;

    return (
        <section className={css.root}>
            <div className={css.content}>
                <h2 className={css.heading}>Neue Aktivitäten</h2>
                <p>Erlebe unsere aktive Community hautnah.</p>
            </div>
            <div className={css.activitiesHolder}>
                {activities
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map(item => (
                        <ActivityItem item={item} key={item.id} />
                    ))}
            </div>
            {!currentUserDefined && (
                <footer className={css.footer}>
                    <Button
                        className={css.subLinkPrimary}
                        type="button"
                        onClick={() =>
                            history.push(
                                createResourceLocatorString('SignupPage', routeConfiguration())
                            )
                        }
                    >
                        Kostenlos registrieren
                    </Button>
                </footer>
            )}
        </section>
    );
};

export default ({ history }) => {
    const [loading, setLoading] = useState(false);
    const [activities, setActivities] = useState(null);

    const requestRecentActivities = async initial => {
        if (initial) setLoading(true);

        try {
            const response = await getRecentMarketplaceEvents(initial);
            const { events } = await response.json();

            setActivities(prevData => {
                if (!Array.isArray(events)) return null;

                const idsDictinonary = (prevData || []).reduce(
                    (acc, item) => ({ ...acc, [item.id]: item.id }),
                    {}
                );
                const newEvents = events.filter(({ id }) => !idsDictinonary[id]);

                return (Array.isArray(prevData) ? [...newEvents, ...prevData] : events).splice(
                    0,
                    6
                );
            });
        } catch (e) {
            setActivities(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        requestRecentActivities(true);
        const interval = 3e4;

        const intervalId = setInterval(() => requestRecentActivities(false), interval);

        return () => clearInterval(intervalId);
    }, []);

    if (loading) return <IconSpinner />;

    const dataAvailable = Array.isArray(activities) && activities.length > 0;

    if (!dataAvailable) return null;

    return <SectionContent activities={activities} history={history} />;
};
